import { SITE_URL, company, products, type RouteSeo } from './site';

export type HeadData = {
  title: string;
  description: string;
  canonical: string | null;
  robots: string;
  meta: { attr: 'name' | 'property'; key: string; content: string }[];
  jsonLd: object;
};

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

const absolute = (path: string) => (path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`);

const postalAddress = (a: typeof company.registeredOffice) => ({
  '@type': 'PostalAddress',
  streetAddress: a.streetAddress,
  addressLocality: a.addressLocality,
  addressRegion: a.addressRegion,
  postalCode: a.postalCode,
  addressCountry: a.addressCountry,
});

const organizationNode = () => ({
  '@type': 'Organization',
  '@id': ORG_ID,
  name: company.legalName,
  alternateName: company.alternateNames,
  url: company.url,
  logo: company.logo,
  image: company.image,
  description: company.description,
  email: company.emails[0],
  telephone: company.phones.office,
  address: postalAddress(company.registeredOffice),
  location: {
    '@type': 'Place',
    name: company.manufacturingUnit.label,
    address: postalAddress(company.manufacturingUnit),
  },
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      telephone: company.phones.mobile,
      email: company.emails[0],
      areaServed: 'Worldwide',
      availableLanguage: ['en', 'hi'],
    },
  ],
  sameAs: company.sameAs,
});

const websiteNode = () => ({
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: company.url,
  name: company.name,
  publisher: { '@id': ORG_ID },
  inLanguage: 'en',
});

export const buildHead = (route: RouteSeo): HeadData => {
  const url = absolute(route.path);
  const canonical = route.indexable ? url : null;
  const robots = route.indexable ? 'index, follow, max-image-preview:large' : 'noindex, nofollow';

  const graph: object[] = [organizationNode(), websiteNode()];

  if (route.indexable) {
    graph.push({
      '@type': route.pageType ?? 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: route.title,
      description: route.description,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': ORG_ID },
      inLanguage: 'en',
      ...(route.breadcrumb ? { breadcrumb: { '@id': `${url}#breadcrumb` } } : {}),
    });

    if (route.breadcrumb) {
      const trail = [{ name: 'Home', path: '/' }, ...route.breadcrumb];
      graph.push({
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: trail.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: absolute(item.path),
        })),
      });
    }

    if (route.product) {
      const p = route.product;
      graph.push({
        '@type': 'Product',
        '@id': `${url}#product`,
        name: `${p.name} API`,
        description: `${p.classification} Available to ${p.pharmacopoeia.join(' / ')} pharmacopoeial standards in ${p.grades.join(' / ')} grade from ${company.legalName}.`,
        category: 'Active Pharmaceutical Ingredient (API)',
        url,
        image: company.image,
        brand: { '@id': ORG_ID },
        manufacturer: { '@id': ORG_ID },
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Pharmacopoeia', value: p.pharmacopoeia.join(' / ') },
          { '@type': 'PropertyValue', name: 'Available Grade', value: p.grades.join(' / ') },
        ],
      });
    }

    if (route.path === '/products/our-products') {
      graph.push({
        '@type': 'ItemList',
        '@id': `${url}#products`,
        name: 'API Product Portfolio',
        itemListElement: products.map((p, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: `${p.name} API`,
          url: absolute(p.path),
        })),
      });
    }
  }

  const ogImage = company.image;
  const meta: HeadData['meta'] = [
    { attr: 'name', key: 'description', content: route.description },
    { attr: 'name', key: 'robots', content: robots },
    { attr: 'property', key: 'og:type', content: route.product ? 'product' : 'website' },
    { attr: 'property', key: 'og:site_name', content: company.name },
    { attr: 'property', key: 'og:title', content: route.title },
    { attr: 'property', key: 'og:description', content: route.description },
    { attr: 'property', key: 'og:url', content: url },
    { attr: 'property', key: 'og:image', content: ogImage },
    { attr: 'property', key: 'og:locale', content: 'en_IN' },
    { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', content: route.title },
    { attr: 'name', key: 'twitter:description', content: route.description },
    { attr: 'name', key: 'twitter:image', content: ogImage },
  ];

  return {
    title: route.title,
    description: route.description,
    canonical,
    robots,
    meta,
    jsonLd: { '@context': 'https://schema.org', '@graph': graph },
  };
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Static <head> markup written into each prerendered HTML file.
export const renderHeadHtml = (head: HeadData) => {
  const lines = [`<title>${escapeHtml(head.title)}</title>`];
  for (const m of head.meta) {
    if (!m.content) continue;
    lines.push(`<meta ${m.attr}="${m.key}" content="${escapeHtml(m.content)}" />`);
  }
  if (head.canonical) lines.push(`<link rel="canonical" href="${head.canonical}" />`);
  const json = JSON.stringify(head.jsonLd).replace(/</g, '\\u003c');
  lines.push(`<script type="application/ld+json" id="seo-jsonld">${json}</script>`);
  return lines.join('\n    ');
};

// Updates the same tags in the browser after client-side navigation.
export const applyHeadToDocument = (head: HeadData) => {
  document.title = head.title;

  for (const m of head.meta) {
    const selector = `meta[${m.attr}="${m.key}"]`;
    let el = document.head.querySelector<HTMLMetaElement>(selector);
    if (!m.content) {
      el?.remove();
      continue;
    }
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(m.attr, m.key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', m.content);
  }

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (head.canonical) {
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = head.canonical;
  } else {
    canonical?.remove();
  }

  let script = document.getElementById('seo-jsonld');
  if (!script) {
    script = document.createElement('script');
    script.id = 'seo-jsonld';
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(head.jsonLd);
};
